import React from 'react';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { AxiosError } from 'axios';

import API from 'utils/api';
import routes from 'core/routes';
import { SLASH_FULL_DATE_FORMAT } from 'core/constants';
import { useStore } from 'utils/hooks/store';
import { useNavBarHeight } from 'utils/hooks/navbar';
import { formatFloating } from 'utils/formatters/timezone';
import { NotFound } from 'containers/Error/Pages';
import {
  AcknowledgementPendingResponse,
  AcknowledgementReceiptResponse,
  AcknowledgementState,
  SubmitAcknowledgementBody,
} from 'utils/api/modules/entities/entities.types';

import { MOBILE_DOCUMENT_HEIGHT } from './constants';
import AcknowledgementDocumentView from './components/AcknowledgementDocumentView';
import AcknowledgePanel from './components/AcknowledgePanel';
import { AcknowledgeActionsPanel, AcknowledgeIntroPanel } from './components/AcknowledgeMobilePanels';
import AcknowledgeConfirmModal from './components/AcknowledgeConfirmModal';
import AcknowledgementReceipt, { ACKNOWLEDGEMENTS_TAB_LINK } from './components/AcknowledgementReceipt';
import {
  Body,
  CrumbLink,
  CrumbRow,
  CrumbSeparator,
  DocTitle,
  Header,
  MetaDot,
  MetaStrip,
  MobileStack,
  PageContainer,
  PanelColumn,
  SkeletonBlock,
} from './styles';

type AckPageState =
  | { status: 'loading' }
  | { status: 'pending'; data: AcknowledgementPendingResponse }
  | { status: 'acknowledged'; data: AcknowledgementReceiptResponse }
  | { status: 'error' };

const PageHeader = ({
  data,
  embedded,
}: {
  data: AcknowledgementPendingResponse;
  embedded: boolean;
}): React.ReactElement => (
  <Header>
    {!embedded && (
      <CrumbRow aria-label="breadcrumb">
        <CrumbLink to={String(routes.dashboard.home)}>Home</CrumbLink>
        <CrumbSeparator>›</CrumbSeparator>
        <CrumbLink to={ACKNOWLEDGEMENTS_TAB_LINK}>Actions</CrumbLink>
      </CrumbRow>
    )}
    <DocTitle>{data.document.name}</DocTitle>
    <MetaStrip>
      <span>Version {data.document.tiptapVersion}</span>
      <MetaDot>·</MetaDot>
      <span>Due date {formatFloating(data.request.dueDate, 'date', SLASH_FULL_DATE_FORMAT)}</span>
      <MetaDot>·</MetaDot>
      <span>Requested by {data.request.owner.name}</span>
    </MetaStrip>
  </Header>
);

const LoadingSkeleton = ({
  navBarHeight,
  embedded,
}: {
  navBarHeight: number;
  embedded: boolean;
}): React.ReactElement => (
  <PageContainer $navBarHeight={navBarHeight}>
    <Header>
      <SkeletonBlock $height="14px" $width="180px" />
      <SkeletonBlock $height="24px" $width="320px" />
      <SkeletonBlock $height="16px" $width="280px" />
    </Header>
    {embedded ? (
      // Mirror the mobile stack: intro card, document, actions card — full width.
      <MobileStack>
        <SkeletonBlock $height="84px" />
        <SkeletonBlock $height={MOBILE_DOCUMENT_HEIGHT} />
        <SkeletonBlock $height="180px" />
      </MobileStack>
    ) : (
      <Body>
        <SkeletonBlock $height="100%" />
        <PanelColumn>
          <SkeletonBlock $height="240px" />
        </PanelColumn>
      </Body>
    )}
  </PageContainer>
);

const AcknowledgementPage = ({ embedded = false }: { embedded?: boolean }): React.ReactElement => {
  const { acknowledgementId } = useParams<{ acknowledgementId: string }>();
  const { session } = useStore();
  const navBarHeight = useNavBarHeight();

  const [pageState, setPageState] = React.useState<AckPageState>({ status: 'loading' });
  const [modalOpen, setModalOpen] = React.useState(false);
  const [hasReadToEnd, setHasReadToEnd] = React.useState(false);

  React.useEffect(() => {
    if (!acknowledgementId) {
      setPageState({ status: 'error' });
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await API.entities.openAcknowledgement(acknowledgementId)();
        if (cancelled) {
          return;
        }
        if (response.data.state === AcknowledgementState.Acknowledged) {
          setPageState({ status: 'acknowledged', data: response.data });
        } else {
          setPageState({ status: 'pending', data: response.data });
        }
      } catch (error) {
        if (cancelled) {
          return;
        }
        setPageState({ status: 'error' });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [acknowledgementId]);

  const getTiptapToken = React.useCallback(async (): Promise<string> => {
    const response = await API.entities.openAcknowledgement(acknowledgementId as string)();
    return response.data.state === AcknowledgementState.Pending ? response.data.tiptapToken : '';
  }, [acknowledgementId]);

  const handleSubmit = async (body: SubmitAcknowledgementBody): Promise<void> => {
    const response = await API.entities
      .submitAcknowledgement(acknowledgementId as string, body)()
      .catch((error: AxiosError) => {
        if (error?.response?.status === 404) {
          // The row is no longer actionable in this view → inline generic error page.
          setModalOpen(false);
          setPageState({ status: 'error' });
          return null;
        }
        // 400 / 409 / 5xx / network: let the modal toast and stay open for a retry.
        throw error;
      });

    if (response) {
      setModalOpen(false);
      setPageState({ status: 'acknowledged', data: response.data });
    }
  };

  if (pageState.status === 'loading') {
    return <LoadingSkeleton navBarHeight={navBarHeight} embedded={embedded} />;
  }

  if (pageState.status === 'error') {
    return <NotFound hideControls={embedded} />;
  }

  if (pageState.status === 'acknowledged') {
    return <AcknowledgementReceipt data={pageState.data} embedded={embedded} />;
  }

  const { data } = pageState;
  const documentView = (
    <AcknowledgementDocumentView
      documentUuid={data.document.uuid}
      documentName={data.document.name}
      tiptapVersion={data.document.tiptapVersion}
      tiptapToken={data.tiptapToken}
      getTiptapToken={getTiptapToken}
      organisation={session.user?.organisation.uuid as string}
      session={session}
      onReadComplete={() => setHasReadToEnd(true)}
    />
  );

  return (
    <PageContainer $navBarHeight={navBarHeight}>
      <PageHeader data={data} embedded={embedded} />
      {embedded ? (
        // Mobile: stack the panel as two cards — intro above the document, actions below.
        <MobileStack>
          <AcknowledgeIntroPanel />
          {documentView}
          <AcknowledgeActionsPanel hasReadToEnd={hasReadToEnd} onAcknowledge={() => setModalOpen(true)} />
        </MobileStack>
      ) : (
        <Body>
          {documentView}
          <PanelColumn>
            <AcknowledgePanel hasReadToEnd={hasReadToEnd} onAcknowledge={() => setModalOpen(true)} />
          </PanelColumn>
        </Body>
      )}
      {modalOpen && (
        <AcknowledgeConfirmModal
          documentName={data.document.name}
          prefilledName={data.prefilledName}
          onClose={() => setModalOpen(false)}
          onSubmit={handleSubmit}
        />
      )}
    </PageContainer>
  );
};

export default observer(AcknowledgementPage);
